import axiosInstance from 'client';
import * as dto from 'client/dto';

// =====================================================================================================================================

function createUserType(userTypeModel: dto.ModifyUserTypeModel): Promise<dto.UserTypeModel> {
  return axiosInstance
    .post(`/api/usertype`, userTypeModel, {
      headers: { 'X-Entity-Type': 'User Type' },
    })
    .then(({ data }) => data);
}

function deleteUserType(id: string): Promise<void> {
  return axiosInstance
    .delete(`/api/usertype/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'User Type' },
    })
    .then(({ data }) => data);
}

function getUserType(id: string): Promise<dto.UserTypeModel> {
  return axiosInstance
    .get(`/api/usertype/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'User Type' },
    })
    .then(({ data }) => data);
}

function updateUserType(
  id: string,
  updateUserTypeModel: dto.ModifyUserTypeModel
): Promise<dto.UserTypeModel> {
  return axiosInstance
    .put(`/api/usertype/${id}`, updateUserTypeModel, {
      headers: { 'X-Entity-Type': 'User Type' },
    })
    .then(({ data }) => data);
}

// =====================================================================================================================================

function createUserGroup(userTypeModel: dto.UserGroupModel): Promise<dto.UserGroupModel> {
  return axiosInstance
    .post(`/api/usergroup`, userTypeModel, {
      headers: { 'X-Entity-Type': 'User Group' },
    })
    .then(({ data }) => data);
}

function deleteUserGroup(id: number): Promise<void> {
  return axiosInstance
    .delete(`/api/usergroup/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'User Group' },
    })
    .then(({ data }) => data);
}

function getUserGroup(id: number): Promise<dto.UserGroupModel> {
  return axiosInstance
    .get(`/api/usergroup/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'User Group' },
    })
    .then(({ data }) => data);
}

function updateUserGroup(
  id: number,
  updateUserGroupModel: dto.UserGroupModel
): Promise<dto.UserGroupModel> {
  return axiosInstance
    .put(`/api/usergroup/${id}`, updateUserGroupModel, {
      headers: { 'X-Entity-Type': 'User Group' },
    })
    .then(({ data }) => data);
}

// =====================================================================================================================================

function checkUserName(username: string): Promise<dto.UserModel> {
  return axiosInstance
    .get(`/api/user/check`, {
      params: { username },
      // headers: { 'X-Entity-Type': 'UserModel' } no need for error notification since we get 409
    })
    .then(({ data }) => data);
}

function createUser(modifyModel: dto.ModifyUserModel): Promise<dto.UserModel> {
  return axiosInstance
    .post(`/api/user`, modifyModel, {
      headers: { 'X-Entity-Type': 'User' },
    })
    .then(({ data }) => data);
}

function deactivateUser(id: string): Promise<void> {
  return axiosInstance
    .put(`/api/user/${id}/deactivate`, null, {
      headers: { 'X-Entity-Type': 'User' },
    })
    .then(({ data }) => data);
}

function getUser(id: string): Promise<dto.UserModel> {
  return axiosInstance
    .get(`/api/user/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'User' },
    })
    .then(({ data }) => data);
}

function getUsers(): Promise<dto.UserModel[]> {
  return axiosInstance
    .get(`/api/user`, {
      params: {},
      headers: { 'X-Entity-Type': 'Users' },
    })
    .then(({ data }) => data);
}

function initializeNewUser(model: dto.UserInitRequestModel): Promise<void> {
  return axiosInstance
    .post(`/initialize`, model, {
      headers: { 'X-Entity-Type': 'void' },
    })
    .then(({ data }) => data);
}

function reactivateUser(id: string): Promise<void> {
  return axiosInstance
    .put(`/api/user/${id}/reactivate`, null, {
      headers: { 'X-Entity-Type': 'User' },
    })
    .then(({ data }) => data);
}

function updateUser(id: string, updateModel: dto.ModifyUserModel): Promise<dto.UserModel> {
  return axiosInstance
    .put(`/api/user/${id}`, updateModel, {
      headers: { 'X-Entity-Type': 'User' },
    })
    .then(({ data }) => data);
}

function resendConfirmationEmail(id: string): Promise<void> {
  return axiosInstance
    .post(`/api/user/${id}/resendconfirmation`, null, {})
    .then(({ data }) => data);
}

function getResetPasswordLink(id: string): Promise<string> {
  return axiosInstance.get(`/api/user/${id}/resetpassword`, null).then(({ data }) => data);
}

function exportUsers(): Promise<void> {
  return axiosInstance.post(`/api/user/export`, {}, {}).then(({ data }) => data);
}

// =====================================================================================================================================

function createDiseaseGroup(
  userGroupModel: dto.CreateCustomDiseaseGroupModel
): Promise<dto.CustomDiseaseGroup> {
  return axiosInstance
    .post(`/api/diseasegroup`, userGroupModel, {
      headers: { 'X-Entity-Type': 'Custom Disease Group' },
    })
    .then(({ data }) => data);
}

function deleteDiseaseGroup(id: number): Promise<void> {
  return axiosInstance
    .delete(`/api/diseasegroup/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'Disease Group' },
    })
    .then(({ data }) => data);
}

function getDiseaseGroup(id: number): Promise<dto.CustomDiseaseGroup> {
  return axiosInstance
    .get(`/api/diseasegroup/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'Disease Group' },
    })
    .then(({ data }) => data);
}

function getCustomDiseaseGroups(): Promise<dto.CustomDiseaseGroup[]> {
  return axiosInstance
    .get(`/api/diseasegroup`, {
      params: {},
      headers: { 'X-Entity-Type': 'Disease Group' },
    })
    .then(({ data }) => data);
}

function updateDiseaseGroup(
  id: number,
  updateUserTypeModel: dto.UpdateCustomDiseaseGroupModel
): Promise<dto.CustomDiseaseGroup> {
  return axiosInstance
    .put(`/api/diseasegroup/${id}`, updateUserTypeModel, {
      headers: { 'X-Entity-Type': 'Disease Group' },
    })
    .then(({ data }) => data);
}

// =====================================================================================================================================

function getDiseaseRelevanceCustomGroups(): Promise<dto.DiseaseGroupModel[]> {
  return axiosInstance
    .get(`/api/diseasegroup/diseaserelevance`, {
      params: {},
      headers: { 'X-Entity-Type': 'DiseaseGroupModel[]' },
    })
    .then(({ data }) => data);
}

function updateDiseaseRelevance(
  id: string,
  diseaseRelevanceSetting: dto.DiseaseRelevanceSettingsModel
): Promise<void> {
  return axiosInstance
    .post(`/api/usertype/${id}/diseaserelevance`, diseaseRelevanceSetting, {
      headers: { 'X-Entity-Type': 'void' },
    })
    .then(({ data }) => data);
}

// =====================================================================================================================================

function getEmailType(id: number): Promise<dto.EmailTypeModel> {
  return axiosInstance
    .get(`/api/emailtype/${id}`, {
      params: { id },
      headers: { 'X-Entity-Type': 'Email Type' },
    })
    .then(({ data }) => data);
}

function getEmailTypes(): Promise<dto.EmailTypeModel[]> {
  return axiosInstance
    .get(`/api/emailtype`, {
      params: {},
      headers: { 'X-Entity-Type': 'Email Types' },
    })
    .then(({ data }) => data);
}

export default {
  createDiseaseGroup,
  deleteDiseaseGroup,
  getDiseaseGroup,
  getCustomDiseaseGroups,
  updateDiseaseGroup,
  getDiseaseRelevanceCustomGroups,
  getEmailType,
  getEmailTypes,
  createUserType,
  deleteUserType,
  getUserType,
  updateUserType,
  updateDiseaseRelevance,
  checkUserName,
  createUser,
  deactivateUser,
  getUser,
  getUsers,
  initializeNewUser,
  reactivateUser,
  updateUser,
  resendConfirmationEmail,
  getResetPasswordLink,
  exportUsers,
  createUserGroup,
  deleteUserGroup,
  getUserGroup,
  updateUserGroup,
};
